import { Suspense, useLayoutEffect, useRef } from 'react'

import { policyKinds } from '@/app/routes/area-layout'
import { AUDIT_PERMISSIONS } from '@/lib/permissions'
import { usePermissionsAt } from '@/lib/use-permissions'
import { Button } from '@/ui/button'
import { LoadingNotice } from '@/ui/loading-notice'
import { RetryNotice } from '@/ui/retry-notice'
import { ASSISTANT_POLICY_TYPE } from '../../api/assistant-policy-api'
import { CONTENT_UPLOAD_POLICY_TYPE } from '../../api/content-upload-policy-api'
import { WHITELABEL_POLICY_TYPE } from '../../api/whitelabel-policy-api'
import type { PolicySummary } from '../../api/policy-catalog-api'
import { usePolicyCatalog } from './policy-catalog-queries'
import { PolicyOrderGroup } from './policy-order-group'
import type { PolicyKindContribution, PolicyRowSelection, PolicyRowIdentity } from './policy-kind'

const entitySettingTypes = new Set([WHITELABEL_POLICY_TYPE, ASSISTANT_POLICY_TYPE, CONTENT_UPLOAD_POLICY_TYPE])

export function PolicyList({
  entityId,
  returnFocus,
  valueTypes,
  onCreate,
  selection,
  resolvedPolicy,
  unavailablePolicyId,
  onAudit,
}: {
  entityId: string
  returnFocus?: () => HTMLElement | null
  valueTypes?: readonly string[]
  onCreate?: (kind: PolicyKindContribution) => void
  selection?: PolicyRowSelection
  resolvedPolicy?: PolicyRowIdentity
  unavailablePolicyId?: string
  onAudit?: (policy: PolicyRowIdentity, trigger: HTMLButtonElement) => void
}) {
  const { can } = usePermissionsAt(entityId)
  const canReadHistory = can(AUDIT_PERMISSIONS.read)
  const policies = usePolicyCatalog(entityId)
  const kinds = policyKinds()
  const cachedPolicies = (policies.data?.pages.flatMap((page) => page.items) ?? [])
    .filter((policy) => !entitySettingTypes.has(policy.valueType) && policy.id !== unavailablePolicyId)
  const groups = new Map<string, PolicySummary[]>()
  if (valueTypes) {
    for (const valueType of valueTypes) {
      const kind = kinds.find((candidate) => candidate.valueType === valueType)
      if (kind && (!kind.readPermission || can(kind.readPermission))) groups.set(valueType, [])
    }
  }
  for (const policy of cachedPolicies) {
    if (valueTypes && !groups.has(policy.valueType)) continue
    const group = groups.get(policy.valueType) ?? []
    group.push(policy)
    groups.set(policy.valueType, group)
  }
  const lastFocused = useRef<HTMLElement | null>(null)
  useLayoutEffect(() => {
    // A closing dialog can return focus to a trigger that this render then removes.
    if (lastFocused.current?.isConnected === false && document.activeElement === document.body) {
      lastFocused.current = null
      returnFocus?.()?.focus()
    }
  })

  return (
    <section className="flex min-w-0 flex-col gap-6" aria-label="Policies" onFocus={(event) => { lastFocused.current = event.target }}>
      {policies.isError && <RetryNotice
        message="Couldn't load the policy list. Retry to refresh it."
        buttonLabel="Retry policy list" disabled={policies.isFetching}
        onRetry={() => void policies.refetch()} />}
      {policies.isLoading && <LoadingNotice message="Loading policies…" />}
      {!policies.isError && policies.data && !policies.hasNextPage
        && Array.from(groups.values()).every((items) => items.length === 0) && !resolvedPolicy && (
        <p className="border-border text-muted-foreground rounded-md border px-3 py-4 text-center text-sm">No policies you can view at this entity.</p>
      )}
      {Array.from(groups, ([valueType, items]) => {
        const visibleItems = policies.isError ? [] : items
        const kind = kinds.find((candidate) => candidate.valueType === valueType)
        if (!kind) return <PolicyOrderGroup key={valueType} entityId={entityId} valueType={valueType} policies={visibleItems}
          canEdit={false} canReadHistory={canReadHistory} complete={!policies.hasNextPage} reloading={false}
          onEdit={() => undefined} onDelete={() => undefined} />
        const Group = kind.list
        return <Suspense key={valueType} fallback={<LoadingNotice message={`Loading ${kind.label.toLowerCase()} policies…`} />}>
          <Group entityId={entityId} policies={visibleItems}
            selection={selection}
            resolvedPolicy={resolvedPolicy?.valueType === valueType ? resolvedPolicy : undefined}
            complete={!policies.isError && !policies.isLoading && !policies.hasNextPage}
            grouped={valueTypes !== undefined}
            onCreate={onCreate && can(kind.managePermission) && (!kind.readPermission || can(kind.readPermission))
              ? () => onCreate(kind) : undefined}
            canReadHistory={canReadHistory} onAudit={onAudit} returnFocus={returnFocus} />
        </Suspense>
      })}
      {!policies.isError && policies.hasNextPage && <Button type="button" variant="outline" size="sm" className="self-start"
        disabled={policies.isFetchingNextPage} onClick={() => void policies.fetchNextPage()}>
        {policies.isFetchingNextPage ? 'Loading…' : 'Load more'}
      </Button>}
    </section>
  )
}
